import { useLayoutEffect, useRef, useState } from 'react';
import type mapboxgl from 'mapbox-gl';
import {
  isVisibleMapOccluder,
  MAP_OCCLUDER_SELECTOR,
  visibleMapGeometry,
  VisibleMapGeometry,
  ScreenRect,
} from './mapVisibleArea';

type Bounds = { sw: [number, number]; ne: [number, number] };
export type ListingViewport = { bounds: Bounds; geometry: VisibleMapGeometry };

/**
 * Measures the visible map area and the bounds the listing fetch needs.
 *
 * Keeps the normal map control layout while browsing mobile results, but
 * reprojects it when a search moves the map beneath the sheet.
 *
 * A pause freezes the snapshot only once one was taken unpaused. Before that
 * (a cold deep link that opens a covering view first, such as the Messages
 * email link) the hook measures as if unpaused, so the list behind the view
 * loads and follows login, layout and camera changes until the first
 * measurement after the view closes. From then on a pause freezes exactly
 * as before (JAA-391).
 */
export function useMapListingViewport(
  map: mapboxgl.Map | null,
  bounds: Bounds | null,
  paused: boolean,
  layoutKey: string,
  preserveLayout = false,
): ListingViewport | null {
  const [viewport, setViewport] = useState<ListingViewport | null>(null);
  const hasUnpausedSnapshot = useRef(false);
  const layout = useRef<{
    map: mapboxgl.Map;
    visible: ScreenRect;
    occluders: ScreenRect[];
  } | null>(null);
  useLayoutEffect(() => {
    if (!map || !bounds || (paused && hasUnpausedSnapshot.current)) return;
    let frame = 0;
    let removed = false;
    const canvas = map.getCanvas();
    const measure = () => {
      if (removed || map.isMoving()) return;
      if (!preserveLayout) {
        const rect = canvas.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const scaleX = canvas.clientWidth / rect.width;
        const scaleY = canvas.clientHeight / rect.height;
        const local = (r: {
          left: number;
          top: number;
          right: number;
          bottom: number;
        }) => ({
          left: (r.left - rect.left) * scaleX,
          right: (r.right - rect.left) * scaleX,
          top: (r.top - rect.top) * scaleY,
          bottom: (r.bottom - rect.top) * scaleY,
        });
        const screen = window.visualViewport;
        const left = screen?.offsetLeft ?? 0;
        const top = screen?.offsetTop ?? 0;
        const visible = local({
          left: Math.max(rect.left, left),
          top: Math.max(rect.top, top),
          right: Math.min(
            rect.right,
            left + (screen?.width ?? window.innerWidth),
          ),
          bottom: Math.min(
            rect.bottom,
            top + (screen?.height ?? window.innerHeight),
          ),
        });
        const occluders = Array.from(
          document.querySelectorAll(MAP_OCCLUDER_SELECTOR),
        )
          .filter(isVisibleMapOccluder)
          .map((el) => local(el.getBoundingClientRect()));
        layout.current = { map, visible, occluders };
      }
      const saved = layout.current;
      if (!saved || saved.map !== map) return;
      const next = {
        bounds,
        geometry: visibleMapGeometry(saved.visible, saved.occluders, (p) =>
          map.unproject(p),
        ),
      };
      if (!paused) hasUnpausedSnapshot.current = true;
      setViewport((previous) =>
        previous?.bounds === bounds &&
        JSON.stringify(previous.geometry) === JSON.stringify(next.geometry)
          ? previous
          : next,
      );
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const observer = new ResizeObserver(schedule);
    observer.observe(canvas);
    document
      .querySelectorAll(MAP_OCCLUDER_SELECTOR)
      .forEach((el) => observer.observe(el));
    // CSS transitions can move overlays without resizing their boxes.
    document.addEventListener('transitionend', schedule, true);
    document.addEventListener('animationend', schedule, true);
    window.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('resize', schedule);
    const onRemove = () => {
      removed = true;
      cancelAnimationFrame(frame);
    };
    map.on('remove', onRemove);
    // The initial measurement (including login/logout layout changes) can
    // happen during a camera move. Retry when it settles even if no bounds or
    // DOM resize change was emitted to the parent.
    map.on('moveend', schedule);
    measure();
    return () => {
      onRemove();
      observer.disconnect();
      map.off('remove', onRemove);
      map.off('moveend', schedule);
      document.removeEventListener('transitionend', schedule, true);
      document.removeEventListener('animationend', schedule, true);
      window.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);
    };
  }, [map, bounds, paused, layoutKey, preserveLayout]);
  return viewport;
}
